import { useRef, useState, type ReactNode } from 'react';
import { ActionSheet } from './Sheet';

export interface SwipeAction {
  label: string;
  tone: 'danger' | 'accent' | 'neutral';
  onSelect: () => void;
}

const TONE = {
  danger: 'bg-red-500',
  accent: 'bg-[var(--accent)]',
  neutral: 'bg-zinc-400 dark:bg-zinc-600'
};

/**
 * Swipe left for `trailing` actions (Delete), right for the `leading` one
 * (Duplicate, Mark paid). A long press offers the same actions in an action
 * sheet, so nothing is reachable only by a gesture. A tap while open closes it.
 */
export function SwipeRow({
  children,
  trailing = [],
  leading,
  title
}: {
  children: ReactNode;
  trailing?: SwipeAction[];
  leading?: SwipeAction;
  /** Shown at the top of the long-press action sheet. */
  title?: string;
}) {
  const tw = trailing.length * 84;
  const lw = leading ? 96 : 0;
  const [x, setX] = useState(0);
  const [menu, setMenu] = useState(false);
  const start = useRef<{ x: number; y: number; base: number; locked?: boolean } | null>(null);
  const press = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);

  const cancelPress = () => {
    if (press.current) clearTimeout(press.current);
    press.current = null;
  };

  return (
    <div className="relative overflow-hidden">
      {leading && x > 0 && (
        <button
          type="button"
          onClick={() => {
            setX(0);
            leading.onSelect();
          }}
          className={`absolute inset-y-0 left-0 flex items-center px-5 text-secondary font-semibold text-white ${TONE[leading.tone]}`}
          style={{ width: Math.max(lw, x) }}
        >
          {leading.label}
        </button>
      )}
      {trailing.length > 0 && x < 0 && (
        <div className="absolute inset-y-0 right-0 flex" style={{ width: Math.max(tw, -x) }}>
          {trailing.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={() => {
                setX(0);
                a.onSelect();
              }}
              className={`flex-1 text-secondary font-semibold text-white ${TONE[a.tone]}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      <div
        className="relative bg-[var(--surface)]"
        style={{ transform: `translateX(${x}px)`, transition: start.current ? 'none' : 'transform 220ms ease' }}
        onContextMenu={(e) => e.preventDefault()}
        onTouchStart={(e) => {
          longPressed.current = false;
          start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, base: x };
          press.current = setTimeout(() => {
            longPressed.current = true;
            start.current = null;
            setX(0);
            navigator.vibrate?.(10);
            setMenu(true);
          }, 500);
        }}
        onTouchMove={(e) => {
          const s = start.current;
          if (!s) return;
          const dx = e.touches[0].clientX - s.x;
          const dy = e.touches[0].clientY - s.y;
          if (Math.abs(dx) > 6 || Math.abs(dy) > 6) cancelPress();
          if (s.locked === undefined) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            s.locked = Math.abs(dx) > Math.abs(dy);
          }
          if (!s.locked) return;
          const next = s.base + dx;
          setX(Math.min(leading ? lw + 60 : 0, Math.max(trailing.length ? -tw - 40 : 0, next)));
        }}
        onTouchEnd={() => {
          cancelPress();
          const s = start.current;
          start.current = null;
          if (!s?.locked) return;
          // A full swipe right runs the leading action; a half one leaves it showing.
          if (leading && x > lw + 40) {
            setX(0);
            leading.onSelect();
          } else if (leading && x > lw / 2) setX(lw);
          else if (x < -tw / 2) setX(-tw);
          else setX(0);
        }}
        onClickCapture={(e) => {
          if (x !== 0 || longPressed.current) {
            e.preventDefault();
            e.stopPropagation();
            setX(0);
            longPressed.current = false;
          }
        }}
      >
        {children}
      </div>
      <ActionSheet
        open={menu}
        onClose={() => setMenu(false)}
        title={title}
        actions={[...(leading ? [leading] : []), ...trailing].map((a) => ({ label: a.label, destructive: a.tone === 'danger', onSelect: a.onSelect }))}
      />
    </div>
  );
}
